/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import * as React from 'react';

import { format, Heading, IComponentNodeProps, IModuleProps, INodeProps, ISingleSlideCarouselProps, NodeTag } from '@msdyn365-commerce-modules/utilities';
import { AffiliationLoyaltyTier, ProductAvailableQuantity, ProductPrice, ProductSearchResult, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';
import { getActivePricesAsync, getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

import ProductCard from '../../components/product.component';
import { publish } from '../../Utilities/analytics/analytics-dispatcher';
import CommerceAttributesParser from '../../Utilities/commerce-attributes-parser';
import SingleSlideCarousel from '../../Utilities/single-slide-carousel';

import { ISmweProductCollectionData } from './smwe-product-collection.data';
import { ISmweProductCollectionProps, layout } from './smwe-product-collection.props.autogenerated';

export interface IProductCollectionViewProps extends ISmweProductCollectionProps<ISmweProductCollectionData> {
    heading?: React.ReactNode;
    ProductCollectionContainer: IModuleProps;
    SingleSlideCarouselComponentProps: INodeProps;
    GridComponentProps: INodeProps;
    products: IProductComponentViewProps[];
    isCarousel: boolean;
}

export interface IProductComponentViewProps {
    ProductContainer: INodeProps;
    productComponent: React.ReactNode;
}

export interface IProductCollectionState {
    clubPricing: ProductPrice[] | undefined;
    productAvailability: ProductAvailableQuantity[] | undefined;
}

/**
 * ProductCollection component
 */
class ProductCollection extends React.PureComponent<ISmweProductCollectionProps<ISmweProductCollectionData>, IProductCollectionState> {

    constructor(props: ISmweProductCollectionProps<ISmweProductCollectionData>, state: IProductCollectionState) {
        super(props);

        this.state = {
            clubPricing: undefined,
            productAvailability: undefined
        };
    }

    public componentDidMount(): void {
        const products = this.props.config.productCollection.products;

        if (products && products.length) {
            void this._getClubPricing(products);
            void this._getProductavailabilty(products);

            const impressions = products.map(product => ({
                product,
                attributes: CommerceAttributesParser.getParsedAttributes(product.AttributeValues || []),
            }));

            publish('impression', {
                products: impressions,

                list: 'Product Collection',      // Could be Search Results if we ever enable search results pages
                category: this.props.config.heading?.text,
                context: this.props.context,
            });

        }
    }

    public render(): JSX.Element | null {
        const { heading, productCollection, className } = this.props.config;
        const { resources } = this.props;
        const products = productCollection && productCollection.products;
        const isCarousel = this.props.config.layout === layout.carousel;
        const headingComponent = heading && heading.text && <Heading className='ms-product-collection__heading' headingTag={heading.tag} text={heading.text} />;

        const productCollectionViewProps = {
            ...this.props,
            heading: headingComponent,
            SingleSlideCarouselComponentProps: {
                tag: SingleSlideCarousel,
                className: '',
                flipperPrevLabel: resources.flipperPrevious,
                flipperNextLabel: resources.flipperNext,
                parentId: this.props.id
            } as IComponentNodeProps<ISingleSlideCarouselProps>,
            ProductCollectionContainer: {
                moduleProps: this.props,
                className: classnames('ms-product-collection ms-search-result-container', className)
            },
            GridComponentProps: {
                tag: 'ul' as NodeTag,
                className: 'ms-product-collection__items',
            },
            isCarousel: isCarousel,
            products: products && products.length > 0 && products.map((item: ProductSearchResult, index: number) => this._getProduct(item, index)),
        };
        return this.props.renderView(productCollectionViewProps) as React.ReactElement;
    }

    private async _getClubPricing(products:ProductSearchResult[]): Promise<void> {
        const projectDomain: ProjectionDomain = { ChannelId: +this.props.context.request.apiSettings.channelId, CatalogId: +this.props.context.request.apiSettings.catalogId };
        let affiliations: AffiliationLoyaltyTier[];
        if (this.props.context.app.config.affiliationId) {
            affiliations = [
                {
                    AffiliationId: this.props.context.app.config.affiliationId
                }
            ];
        } else {
            affiliations = [];
        }
        const productIds: number[] = [];
        products.forEach((product) => {
            productIds.push(product.RecordId);
        });
        const clubPricingAll = await getActivePricesAsync(
                { callerContext: this.props.context.actionContext },
                projectDomain,
                productIds,
                new Date(),
                '',
                affiliations,
                true
            );
        this.setState({clubPricing: clubPricingAll});
    }

    private async _getProductavailabilty(products: ProductSearchResult[]): Promise<void> {
        if (products && products.length && products.length > 0) {
            const productIds: number[] = [];
            products.forEach((product) => {
                productIds.push(product.RecordId);
            });
            const availabilityAll = await getEstimatedAvailabilityAsync({ callerContext: this.props.context.actionContext }, { ProductIds: productIds, DefaultWarehouseOnly: true});
            const formattedResponse = availabilityAll.ProductWarehouseInventoryAvailabilities?.map((product) => {
                return {ProductId: product.ProductId, AvailableQuantity: product.PhysicalAvailable, ExtensionProperties: product.ExtensionProperties};
            }) as ProductAvailableQuantity[];
            this.setState({productAvailability: formattedResponse});
        }
    }

    private _getProduct = (product: ProductSearchResult, index: number): IProductComponentViewProps => {
        const { imageSettings, showPrice } = this.props.config;
        const addToCart = this.props.config.addToCart || false;
        const resources = this.props.resources;
        const categoryHierarchy = undefined;
        const passedProps = { ...this.props, categoryHierarchy };
        const availibity = this.state.productAvailability?.find((item) => {
            return item.ProductId === product.RecordId;
        });
        const clubPrice = this.state.clubPricing?.find((item) => {
            return item.ProductId === product.RecordId;
        });
        const showQuantityAsDropdown = this.props.config.showQuantityAsDropdown || false;

        return {
            ProductContainer: {
                tag: 'li' as NodeTag,
                className: 'ms-product-collection__item ms-product-search-result__item',
                role: 'listitem',
                key: index,
            },
            productComponent: (
                <ProductCard
                    context={this.props.context}
                    imageSettings={imageSettings}
                    freePriceText={resources.priceFree}
                    originalPriceText={resources.originalPriceText}
                    currentPriceText={resources.currentPriceText}
                    ratingAriaLabel={format(resources.ratingAriaLabel, product.AverageRating, 5)}
                    id={this.props.id}
                    key={product.RecordId}
                    typeName={this.props.typeName}
                    data={{ product: product }}
                    parentProps={passedProps}
                    showPrice={showPrice}
                    addToCart={addToCart}
                    addToCartText={resources.addToCartText}
                    addToCartMessage={resources.addToCartMessage}
                    giftCardText={resources.giftCardText}
                    cart={this.props.data.cart}
                    displayQuantitySlider={addToCart}
                    clubPrice={clubPrice} // won't show clubPrice if addToCart is false
                    availability={availibity}
                    showQuantityAsDropdown={showQuantityAsDropdown}
                />
            )
        };
    };
}

export default ProductCollection;
